import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { useDate, useT } from '../i18n/index.js';
import { useSession } from '../store/session.js';
import { Badge, Banner, Button, Card, Spinner } from '../components/ui.js';

/** Version del texto legal aceptado. Sin ella, un consentimiento no es demostrable. */
const POLICY_VERSION = '2026-08';

export function AccountPage() {
  const t = useT();
  const formatDate = useDate();
  const { user, login } = useSession();
  const [consents, setConsents] = useState<Array<{ kind: string; granted: boolean; at: string; policyVersion: string }>>([]);
  const [requests, setRequests] = useState<Array<{ id: string; type: string; status: string; dueAt: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      void login('/cuenta');
      return;
    }
    Promise.all([
      api.listConsents(),
      fetch('/api/rgpd/requests', { credentials: 'include' }).then((r) => r.json()),
    ])
      .then(([c, r]) => {
        setConsents(c.items);
        setRequests((r as { items: typeof requests }).items ?? []);
      })
      .finally(() => setLoading(false));
  }, [user, login]);

  if (!user || loading) return <Spinner label={t('app.loading')} />;

  // El ultimo consentimiento de cada tipo es el vigente: el historial NO se
  // sobreescribe, para poder demostrar que en tal fecha se acepto tal texto.
  const current = (kind: string) => consents.find((c) => c.kind === kind);

  const toggle = async (kind: string, granted: boolean) => {
    await api.grantConsent(kind, granted, POLICY_VERSION);
    setConsents([{ kind, granted, at: new Date().toISOString(), policyVersion: POLICY_VERSION }, ...consents]);
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4 px-4 py-6">
      <h1 className="text-2xl font-semibold">{t('rgpd.title')}</h1>

      <Card>
        <p className="font-medium">{user.name}</p>
        <p className="text-sm text-tinta-suave">{user.email}</p>
        {user.besbelloSub && <Badge tone="ok">Cuenta Besbello vinculada</Badge>}
      </Card>

      <Card className="space-y-3">
        <h2 className="text-sm font-semibold">{t('rgpd.consents')}</h2>

        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            checked={current('marketing')?.granted ?? false}
            onChange={(e) => void toggle('marketing', e.target.checked)}
            className="mt-1"
          />
          <span>
            <span className="block">{t('rgpd.marketing')}</span>
            {current('marketing') && (
              <span className="block text-xs text-tinta-suave">
                {formatDate(current('marketing')!.at)} · v{current('marketing')!.policyVersion}
              </span>
            )}
          </span>
        </label>

        <Banner tone="info" title={t('rgpd.photoUpload')}>
          {t('rgpd.photoNotice')}
        </Banner>
      </Card>

      <Card className="space-y-3">
        <h2 className="text-sm font-semibold">{t('rgpd.requests')}</h2>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={async () => {
              const data = await api.exportMyData();
              const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = 'besbello-datos.json';
              link.click();
              URL.revokeObjectURL(url);
            }}
          >
            {t('rgpd.export')}
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              await api.createDsr('erasure');
              setMessage('Solicitud registrada. Tienes respuesta en un mes como máximo.');
            }}
          >
            {t('rgpd.erase')}
          </Button>
        </div>

        {message && <Banner tone="ok" title={message} />}

        {requests.length > 0 && (
          <ul className="space-y-1 text-sm">
            {requests.map((request) => (
              <li key={request.id} className="flex items-center justify-between rounded-lg border border-borde p-2">
                <span>{request.type}</span>
                <span className="flex items-center gap-2">
                  <Badge>{request.status}</Badge>
                  <span className="text-xs text-tinta-suave">{formatDate(request.dueAt)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
